import { StatusDot } from '@components/StatusDot';
import { Column, Table } from '@components/Table';
import {
  useOrganizationInvitations,
  useOrganizationMembers,
  useRemindInvitation,
  useRevokeInvitation,
} from '@hooks/apiHooks/userHooks';
import { formatDateTime } from '@utils/timeFormatter';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { IInvitation, IOrganizationMember } from '../../../../api/user';
import { OidcUser } from '../../../../types/oidcUser';
import { showToast } from '../../../../utils/toast';
import InviteMemberModal from './InviteMemberModal';
import RemoveMemberModal from './RemoveMemberModal';

interface IMembersTabProps {
  orgId: string;
  isCurrentUserAdmin: boolean;
}

const MembersTab: FC<IMembersTabProps> = ({ orgId, isCurrentUserAdmin }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useOrganizationMembers(orgId);
  const { data: invitationsData } = useOrganizationInvitations(orgId);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] =
    useState<IOrganizationMember | null>(null);
  const { user } = useAuth();
  const currentUserId = (user as OidcUser)?.profile?.sub || '';

  const members = data?.items || [];

  const { mutate: remindInvitation } = useRemindInvitation();
  const { mutate: revokeInvitation } = useRevokeInvitation();
  const [processingInviteId, setProcessingInviteId] = useState<string | null>(
    null,
  );

  const handleRemindInvitation = useCallback(
    (inviteId: string) => {
      setProcessingInviteId(inviteId);
      remindInvitation(
        { organizationId: orgId, inviteId },
        {
          onSuccess: () => {
            showToast.success(t('account.reminderSent'));
            setProcessingInviteId(null);
          },
          onError: (error: any) => {
            const errorMessage =
              error?.response?.data?.message ||
              error?.message ||
              t('account.reminderFailed');
            showToast.error(errorMessage);
            setProcessingInviteId(null);
          },
        },
      );
    },
    [orgId, remindInvitation],
  );

  const handleRevokeInvitation = useCallback(
    (inviteId: string) => {
      setProcessingInviteId(inviteId);
      revokeInvitation(
        { organizationId: orgId, inviteId },
        {
          onSuccess: () => {
            showToast.success(t('account.invitationRevoked'));
            setProcessingInviteId(null);
          },
          onError: (error: any) => {
            const errorMessage =
              error?.response?.data?.message ||
              error?.message ||
              t('account.revokeFailed');
            showToast.error(errorMessage);
            setProcessingInviteId(null);
          },
        },
      );
    },
    [orgId, revokeInvitation],
  );

  const memberColumns: Column<IOrganizationMember>[] = useMemo(() => {
    const columns: Column<IOrganizationMember>[] = [
      {
        key: 'user',
        header: t('account.user'),
        width: '10%',
        render: (member) => (
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium truncate">
                {member.firstName} {member.lastName}
              </span>

              {member.isOwner && (
                <span className="px-2 py-0.5 text-xs font-semibold bg-blue-500 text-white rounded">
                  {t('account.owner')}
                </span>
              )}
            </div>
            <span className="text-xs text-white/60 truncate">
              {member.email}
            </span>
          </div>
        ),
      },
      {
        key: 'state',
        header: t('account.status'),
        width: '7%',
        align: 'left',
        render: (member) => (
          <div className="flex gap-2 items-center">
            <StatusDot
              color={member.state === 'active' ? 'green' : 'gray'}
              statusText={member.state}
              tooltipId={`member-status-${member.id}`}
            />
            <div className="capitalize text-xs">{member.state}</div>
          </div>
        ),
      },
      {
        key: 'roles',
        header: t('account.role'),
        width: '5%',
        align: 'left',
        render: (member) => (
          <span className="capitalize">
            {member.roles?.[0]?.name || t('account.noRole')}
          </span>
        ),
      },
    ];

    if (isCurrentUserAdmin) {
      columns.push({
        key: 'actions',
        header: t('account.actions'),
        width: '5%',
        align: 'right',
        render: (member) => {
          const isCurrentUser = member.id === currentUserId;
          const isAdmin = member.roles?.some((role) => role.name === 'admin');

          if (isCurrentUser || isAdmin) {
            return null;
          }

          return (
            <div className="flex gap-2 justify-end">
              <button
                className="px-3 py-1 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded transition-colors"
                onClick={() => {
                  setMemberToRemove(member);
                  setIsRemoveModalOpen(true);
                }}>
                {t('common.delete')}
              </button>
            </div>
          );
        },
      });
    }

    return columns;
  }, [currentUserId, isCurrentUserAdmin]);

  const invitationColumns: Column<IInvitation>[] = useMemo(() => {
    const inviteStatusColorMap: Record<string, string> = {
      pending: 'yellow',
      accepted: 'green',
      revoked: 'red',
      expired: 'gray',
    };
    const columns: Column<IInvitation>[] = [
      {
        key: 'user',
        header: t('account.user'),
        width: '10%',
        render: (invitation) => (
          <div className="flex flex-col min-w-0">
            <span className="font-medium truncate">
              {invitation.invitee.firstName} {invitation.invitee.lastName}
            </span>
            <span className="text-xs text-white/60 truncate">
              {invitation.invitee.email}
            </span>
          </div>
        ),
      },
      {
        key: 'status',
        header: t('account.status'),
        width: '7%',
        align: 'left',
        render: (invitation) => (
          <div className="flex gap-2 items-center">
            <StatusDot
              color={inviteStatusColorMap[invitation.status]}
              statusText={invitation.status}
              tooltipId={`invitation-status-${invitation.id}`}
            />
            <div className="capitalize text-xs">{invitation.status}</div>
          </div>
        ),
      },
      {
        key: 'lastInvited',
        header: t('account.lastInvited'),
        width: '5%',
        align: 'left',
        render: (invitation) => (
          <div className="flex gap-2 items-center">
            <div className="capitalize text-xs">
              {formatDateTime(invitation.createdAt)}
            </div>
          </div>
        ),
      },
    ];

    if (isCurrentUserAdmin) {
      columns.push({
        key: 'actions',
        header: t('account.actions'),
        width: '5%',
        align: 'right',
        render: (invitation) => {
          if (invitation.status !== 'pending') {
            return null;
          }

          const isProcessing = processingInviteId === invitation.id;

          return (
            <div className="flex gap-2 justify-end">
              <button
                className="px-3 py-1 text-xs bg-white hover:bg-[#F2F2F2] text-black rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleRemindInvitation(invitation.id)}
                disabled={isProcessing}>
                {t('account.remind')}
              </button>
              <button
                className="px-3 py-1 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleRevokeInvitation(invitation.id)}
                disabled={isProcessing}>
                {t('account.revoke')}
              </button>
            </div>
          );
        },
      });
    }

    return columns;
  }, [
    processingInviteId,
    handleRemindInvitation,
    handleRevokeInvitation,
    isCurrentUserAdmin,
  ]);

  const invitations = invitationsData?.items || [];

  return (
    <div className="text-left space-y-8">
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-white">{t('account.allMembers')}</h3>
          {isCurrentUserAdmin && (
            <button
              className="h-[44px] px-6 bg-white hover:bg-[#F2F2F2] transition-colors duration-200 text-black rounded-[16px] font-medium font-[NapsterSans] whitespace-nowrap flex items-center justify-center"
              onClick={() => setIsInviteModalOpen(true)}>
              {t('account.inviteMember')}
            </button>
          )}
        </div>
        <Table
          columns={memberColumns}
          data={members}
          keyExtractor={(member) => member.id}
          isLoading={isLoading}
          emptyMessage={t('account.noMembersFound')}
        />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-white mb-4">{t('account.invitations')}</h3>
        <Table
          columns={invitationColumns}
          data={invitations}
          keyExtractor={(invitation) => invitation.id}
          isLoading={isLoading}
          emptyMessage={t('account.noPendingInvitations')}
        />
      </div>

      <InviteMemberModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        organizationId={orgId}
      />

      <RemoveMemberModal
        isOpen={isRemoveModalOpen}
        onClose={() => {
          setIsRemoveModalOpen(false);
          setMemberToRemove(null);
        }}
        member={memberToRemove}
        organizationId={orgId}
      />
    </div>
  );
};

export default MembersTab;
